export function formatCalendarCellDate(dateInput) {
    const d = new Date(dateInput);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function getMonthKeyFromDateValue(dateInput) {
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return `${date.getFullYear()}-${date.getMonth()}`;
}

export function getSafeDisplayDate(value) {
    if (!value) return null;

    if (value instanceof Date) {
        const parsed = new Date(value.getFullYear(), value.getMonth(), value.getDate());
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const stringValue = String(value).trim();
    const parts = stringValue.split("-");

    if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        const parsed = new Date(year, month - 1, day);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(stringValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getLocalDateString(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return "";
    }

    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
}

export function getWeekKeyFromDate(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return null;
    }

    const dayOfWeek = parsed.getDay();
    const diff = parsed.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(parsed);
    monday.setDate(diff);

    return `${monday.getFullYear()}-${monday.getMonth()}-${monday.getDate()}`;
}

export function getMondayForDate(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return null;
    }

    const dayOfWeek = parsed.getDay();
    const diff = parsed.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(parsed);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);

    return monday;
}

export function getMonthGridStartDate(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    return getMondayForDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
}

export function getMonthGridEndDate(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    const monthGridStart = getMonthGridStartDate(monthDate);
    if (!monthGridStart) {
        return null;
    }

    const gridEnd = new Date(monthGridStart);
    gridEnd.setDate(monthGridStart.getDate() + 41);
    gridEnd.setHours(0, 0, 0, 0);

    return gridEnd;
}

export function getFirstMondayWithinMonth(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const firstMonday = getMondayForDate(firstDayOfMonth);

    if (!firstMonday) {
        return null;
    }

    if (firstMonday.getMonth() !== monthDate.getMonth()) {
        firstMonday.setDate(firstMonday.getDate() + 7);
    }

    firstMonday.setHours(0, 0, 0, 0);
    return firstMonday;
}

export function buildMonthWeekStartDates(monthDateValue, options = {}) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return [];
    }

    const today = getSafeDisplayDate(options.today ?? new Date());
    const maxWeeks = Number(options.maxWeeks) > 0 ? Number(options.maxWeeks) : 6;
    const monthGridStart = getMonthGridStartDate(monthDate);
    const monthGridEnd = getMonthGridEndDate(monthDate);

    if (!monthGridStart || !monthGridEnd) {
        return [];
    }

    const isCurrentMonth = !!today &&
        today.getFullYear() === monthDate.getFullYear() &&
        today.getMonth() === monthDate.getMonth();

    const currentWeekMonday = today ? getMondayForDate(today) : null;
    const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    let startWeek = monthGridStart;
    let endWeek = getMondayForDate(monthGridEnd) || monthGridEnd;

    if (isCurrentMonth && currentWeekMonday && currentWeekMonday > monthGridStart) {
        startWeek = currentWeekMonday;
        endWeek = getMondayForDate(endOfMonth) || endOfMonth;
    }

    const weekStarts = [];
    const cursor = new Date(startWeek);

    for (let index = 0; index < maxWeeks && cursor <= endWeek; index += 1) {
        weekStarts.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 7);
    }

    return weekStarts;
}

export function buildMonthWeekData(monthDateValue, scheduleItems = []) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return {};
    }

    const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const monthWeekData = {};

    const cursor = new Date(startOfMonth);
    while (cursor <= endOfMonth) {
        const weekKey = getWeekKeyFromDate(cursor);
        if (weekKey && !monthWeekData[weekKey]) {
            monthWeekData[weekKey] = [];
        }
        cursor.setDate(cursor.getDate() + 1);
    }

    (Array.isArray(scheduleItems) ? scheduleItems : []).forEach((scheduleItem) => {
        const weekKey = getWeekKeyFromDate(scheduleItem?.BookingDate);
        if (!weekKey) {
            return;
        }

        if (!monthWeekData[weekKey]) {
            monthWeekData[weekKey] = [];
        }

        monthWeekData[weekKey].push(scheduleItem);
    });

    return monthWeekData;
}

export function formatDateString(dateString) {
    if (!dateString) {
        return null;
    }

    const date = getSafeDisplayDate(dateString);

    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}
